import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit } from '@angular/core';
import { FlyoutOption } from 'dap-ui-library';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { first } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { removePerson } from '../../../core/states/offer/offer.actions';
import { Router } from '@angular/router';
import { DateService } from '../../../core/services/date.service';
import { AddressState, OfferState } from '../../../core/states/offer/offer.state';
import {
  getIdOfFirstPersonWithoutProducts,
  getOfferState,
  hasFrameworkContract,
  isTupCustomer,
} from '../../../core/states/offer/offer.selector';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  dateOfBirthPatternValidator,
  firstNamePatternValidator,
  personValidator,
} from '../../../core/utils/validators';
import { isNotNullOrUndefined } from '../../../core/utils/typeUtils';
import { KeyDataService } from '../services/key-data.service';
import { Address, KeyDataFormData, PreventNavigationPopupType } from '../key-data.types';
import { PersonDataFormGroup } from '../../../shared/person-data/person-data.forms';
import { GenderOrPrenatal } from '../../../core/api';
import { Observable, withLatestFrom } from 'rxjs';

type KeyDataFormGroup = FormGroup<{
  persons: FormArray<PersonDataFormGroup>;
  address: FormControl<FlyoutOption<Address> | null>;
  saleOfferBegin: FormControl<string>;
  existingCustomer: FormControl<boolean | null>;
}>;

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'ssc-key-data-page',
  templateUrl: './key-data-page.component.html',
  styleUrls: ['./key-data-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KeyDataPageComponent implements OnInit {
  public formGroup: KeyDataFormGroup = new FormGroup({
    persons: new FormArray<PersonDataFormGroup>([]),
    address: new FormControl<FlyoutOption<Address> | null>(null, Validators.required),
    saleOfferBegin: new FormControl<string>(this.getSaleOfferBeginDate(), {
      nonNullable: true,
      validators: Validators.required,
    }),
    existingCustomer: new FormControl<boolean | null>(null),
  });
  public showValidations: boolean = false;
  public showPopupType?: PreventNavigationPopupType;
  public hasFrameworkContract$: Observable<boolean>;
  public isTupCustomer$: Observable<boolean>;
  public scrollToErrorEventEmitter: EventEmitter<void> = new EventEmitter();

  constructor(
    private store: Store,
    private router: Router,
    private keyDataService: KeyDataService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.store
      .select(getOfferState)
      .pipe(first(), untilDestroyed(this))
      .subscribe((offerState) => {
        this.patchForm(offerState);
      });

    this.hasFrameworkContract$ = this.store.select(hasFrameworkContract);
    this.isTupCustomer$ = this.store.select(isTupCustomer);
  }

  private static mapToAddressOption(address: AddressState): FlyoutOption<Address> {
    return {
      value: {
        bfsNumber: address.bfsNumber,
        zipCode: address.zipCode,
        city: address.city,
      },
      label: `${address.zipCode} ${address.city}`,
    };
  }

  private static getPersonFormGroup(): PersonDataFormGroup {
    return new FormGroup(
      {
        personId: new FormControl<number | null>(null),
        gender: new FormControl<GenderOrPrenatal | null>(null, Validators.required),
        dateOfBirth: new FormControl<string | null>(null, [Validators.required, dateOfBirthPatternValidator]),
        firstName: new FormControl<string | null>(null, firstNamePatternValidator),
      },
      personValidator,
    );
  }

  public validateForm(): boolean {
    const isValid = !this.formGroup.invalid;
    if (!isValid) {
      this.showValidations = true;
      this.scrollToErrorEventEmitter.emit();
    }
    return isValid;
  }

  public getKeyDataForm(): KeyDataFormData {
    return this.formGroup.value as KeyDataFormData;
  }

  public closePopup(): void {
    this.showPopupType = undefined;
  }

  public tryUpdateStoreAndNavigate(): void {
    if (!this.validateForm()) {
      return;
    }
    this.keyDataService
      .getKeyDataChangeType$(this.getKeyDataForm())
      .pipe(first(), untilDestroyed(this))
      .subscribe((changeType) => {
        if (changeType === 'NOTHING_CHANGED') {
          this.updateStoreAndNavigate();
        } else {
          this.showPopupType = changeType;
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  public updateStoreAndNavigate(): void {
    this.keyDataService.saveFormData(this.getKeyDataForm());
    this.navigateToProducts();
  }

  public addPerson(): void {
    this.formGroup.controls.persons.push(KeyDataPageComponent.getPersonFormGroup());
  }

  public removePerson(index: number, { value: person }: PersonDataFormGroup): void {
    this.formGroup.controls.persons.removeAt(index);
    if (isNotNullOrUndefined(person.personId)) {
      this.store.dispatch(removePerson({ personId: person.personId }));
    }
  }

  public navigateToProducts(): void {
    this.store
      .select(getIdOfFirstPersonWithoutProducts)
      .pipe(withLatestFrom(this.store.select(isTupCustomer)), first(), untilDestroyed(this))
      .subscribe(([personId, tupCustomer]) => {
        if (tupCustomer) {
          this.router.navigate([`supplementary-insurance`, 0]);
        } else {
          this.router.navigate(['basic-insurance', personId ?? 0]);
        }
      });
  }

  private patchForm({ persons, address, existingCustomer, saleOfferBegin }: OfferState): void {
    if (persons.length > 0 && address !== undefined) {
      persons.forEach(() => this.addPerson());
      this.formGroup.patchValue({
        persons: persons.map((person) => ({
          personId: person.personId,
          gender: person.details.genderOrPrenatal,
          dateOfBirth: DateService.formatDateForView(person.details.dateOfBirth),
          firstName: person.details.firstName,
        })),
        address: KeyDataPageComponent.mapToAddressOption(address),
        existingCustomer,
        saleOfferBegin,
      });
    } else {
      this.addPerson();
    }
  }

  private getSaleOfferBeginDate(): string {
    const date = new Date();
    return `${date.getFullYear() + 1}-01-01`;
  }
}
